import { Head, usePage } from "@inertiajs/react";
import classNames from "classnames";
import { memo, useState, useRef, useEffect, createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HeaderHeightContext } from "/utils/header-height-context";
import useScrolled from "/utils/use-scrolled";

import HamburgerIcon from "/assets/hamburger.svg";
import CrossIcon from "/assets/close.svg";

const TitleContext = createContext(null);

export function Title({ children }) {
  const { setTitleContent } = useContext(TitleContext);

  useEffect(() => {
    setTitleContent(children);
    return () => setTitleContent(null);
  }, [children, setTitleContent]);

  return null;
}

function TitleContextProvider({ children }) {
  const [titleContent, setTitleContent] = useState(null);

  const contextValue = useMemo(() => ({
    titleContent,
    setTitleContent,
  }), [titleContent]);

  return (
    <TitleContext.Provider value={contextValue}>
      {children}
    </TitleContext.Provider>
  );
}

const TitleDisplay = memo(() => {
  const { titleContent } = useContext(TitleContext);
  const { props } = usePage();
  const { title } = props;
  const isScrolled = useScrolled();

  if (titleContent) {
    return titleContent;
  } else {
    return (<h1 className={classNames("header-title", { "header-title--scrolled": isScrolled })}>{title}</h1>);
  }
});

export default function Layout({
  children,
}) {
  const { t } = useTranslation();
  const { url, props } = usePage();
  const { title, locale, menu_items, contact_links, info_links } = props;
  const desktopHeaderRef = useRef();
  const mobileHeaderRef = useRef();
  const currentYear = new Date().getFullYear();

  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const toggleHamburgerMenuOpen = (event) => {
    event.preventDefault();
    setHamburgerMenuOpen(currentState => !currentState);
  };

  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    // Only one of the two headers is visible at a time (they swap at the 976px
    // breakpoint), so whichever currently has a height is the one to measure.
    // borderBoxSize is fractional, unlike offsetHeight — rounding it would leave
    // a hairline gap under the header.
    const measure = (entries) => {
      for (let entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
        if (height > 0) setHeaderHeight(height);
      }
    };

    const observer = new ResizeObserver(measure);

    if (desktopHeaderRef.current) observer.observe(desktopHeaderRef.current);
    if (mobileHeaderRef.current) observer.observe(mobileHeaderRef.current);

    return () => observer.disconnect();
  }, []);

  const localeUrl = (targetLocale) => {
    if (url.match(`/${locale}/`))
      return url.replace(`/${locale}/`, `/${targetLocale}/`);

    if (url.match(`locale=${locale}`))
      return url.replace(`locale=${locale}`, `locale=${targetLocale}`);
  };

  return (
    <HeaderHeightContext.Provider value={headerHeight}>
      <TitleContextProvider>
        <Head>
          <title>{title}</title>

          <meta key="og:site_name" property="og:site_name" content={t("opengraph.siteName")} />
          <meta head-key="og:type" key="og:type" property="og:type" content="website" />
          <meta head-key="og:title" key="og:title" property="og:title" content={t("opengraph.title")} />

          <meta head-key="twitter:card" key="twitter:card" name="twitter:card" content="summary" />
          <meta head-key="twitter:title" key="twitter:title" name="twitter:title" content={t("opengraph.title")} />
        </Head>

        <header className="desktop" ref={desktopHeaderRef}>
          <TitleDisplay />

          <nav>
            {menu_items.works && (
              <a href={`/${locale}${menu_items.works.value}`} className="nav-link">{menu_items.works[`title_${locale}`]}</a>
            )}
            {menu_items.team && (
              <a href={`/${locale}${menu_items.team.value}`} className="nav-link">{menu_items.team[`title_${locale}`]}</a>
            )}
            {menu_items.about && (
              <a href={`/${locale}${menu_items.about.value}`} className="nav-link">{menu_items.about[`title_${locale}`]}</a>
            )}
            {menu_items.contacts && (
              <a href={`/${locale}${menu_items.contacts.value}`} className="nav-link">{menu_items.contacts[`title_${locale}`]}</a>
            )}

            <span>
              {locale === "ru" ? (
                <span className="txt-gray-dark">Ру</span>
              ) : (
                <a href={localeUrl("ru")} className="nav-link">Ру</a>
              )}
              &nbsp;/&nbsp;
              {locale === "en" ? (
                <span className="txt-gray-dark">En</span>
              ) : (
                <a href={localeUrl("en")} className="nav-link">En</a>
              )}
            </span>
          </nav>
        </header>

        <header className="mobile" ref={mobileHeaderRef}>
          <a href="/" className="logo">
            {t('common.sitename')}
          </a>

          <a href="#" onClick={toggleHamburgerMenuOpen} className="hamburger">
            <img src={HamburgerIcon} />
          </a>

          <a href="#" className="cross hidden">
            <img src={CrossIcon} />
          </a>
        </header>

        <div
          className={classNames("hamburger-screen", { "hamburger-screen--hidden": !hamburgerMenuOpen })}
          style={{ top: headerHeight + 'px', height: `calc(100vh - ${headerHeight}px)` }}
        >
          <nav>
            <a href={`/${locale}/projects`} className="nav-link">{t("layout.menu.works")}</a>
            <a href={`/${locale}/team`} className="nav-link">{t("layout.menu.team")}</a>
            <a href={`/${locale}/about`} className="nav-link">{t("layout.menu.aboutUs")}</a>
            <a href={`/${locale}/contacts`} className="nav-link">{t("layout.menu.contacts")}</a>
          </nav>

          <div className="contacts">
            {info_links.email && (
              <a
                href={info_links.email.value}
                className="nav-link"
              >{info_links.email[`title_${locale}`]}</a>
            )}

            <div className="social-links">
              {contact_links.telegram && (
                <a href={contact_links.telegram.value} className="nav-link" target="_blank">{contact_links.telegram[`title_${locale}`]}</a>
              )}
              {contact_links.instagram && (
                <a href={contact_links.instagram.value} className="nav-link" target="_blank">{contact_links.instagram[`title_${locale}`]}</a>
              )}
              {contact_links.behance && (
                <a href={contact_links.behance.value} className="nav-link" target="_blank">{contact_links.behance[`title_${locale}`]}</a>
              )}
              {contact_links.facebook && (
                <a href={contact_links.facebook.value} className="nav-link" target="_blank">{contact_links.facebook[`title_${locale}`]}</a>
              )}
              {contact_links.linkedin && (
                <a href={contact_links.linkedin.value} className="nav-link" target="_blank">{contact_links.linkedin[`title_${locale}`]}</a>
              )}
            </div>
          </div>

          <span className="lang-switchers">
            {locale === "ru" ? (
              <span className="txt-gray-dark">Ру</span>
            ) : (
              <a href={localeUrl("ru")} className="nav-link">Ру</a>
            )}
            &nbsp;/&nbsp;
            {locale === "en" ? (
              <span className="txt-gray-dark">En</span>
            ) : (
              <a href={localeUrl("en")} className="nav-link">En</a>
            )}
          </span>
        </div>

        <main style={{ marginTop: headerHeight + 'px' }}>
          {children}
        </main>

        <footer className="desktop">
          <div className="left">
            <span>© {currentYear}</span>
            {info_links.privacy_policy && (
              <a
                href={`/${locale}${info_links.privacy_policy.value}`}
                className="nav-link"
              >{info_links.privacy_policy[`title_${locale}`]}</a>
            )}
            {info_links.email && (
              <a
                href={info_links.email.value}
                className="nav-link"
              >{info_links.email[`title_${locale}`]}</a>
            )}
          </div>

          <div className="social-links">
            {contact_links.telegram && (
              <a href={contact_links.telegram.value} className="nav-link" target="_blank">{contact_links.telegram[`title_${locale}`]}</a>
            )}
            {contact_links.instagram && (
              <a href={contact_links.instagram.value} className="nav-link" target="_blank">{contact_links.instagram[`title_${locale}`]}</a>
            )}
            {contact_links.behance && (
              <a href={contact_links.behance.value} className="nav-link" target="_blank">{contact_links.behance[`title_${locale}`]}</a>
            )}
            {contact_links.facebook && (
              <a href={contact_links.facebook.value} className="nav-link" target="_blank">{contact_links.facebook[`title_${locale}`]}</a>
            )}
            {contact_links.linkedin && (
              <a href={contact_links.linkedin.value} className="nav-link" target="_blank">{contact_links.linkedin[`title_${locale}`]}</a>
            )}
          </div>
        </footer>

        <footer className="mobile">
          <div className="contacts">
            {info_links.email && (
              <a
                href={info_links.email.value}
                className="nav-link"
              >{info_links.email[`title_${locale}`]}</a>
            )}
          </div>

          <div className="end">
            <span>© {currentYear}</span>
            {info_links.privacy_policy && (
              <a
                href={`/${locale}${info_links.privacy_policy.value}`}
                className="nav-link"
              >{info_links.privacy_policy[`title_${locale}`]}</a>
            )}
          </div>
        </footer>
      </TitleContextProvider>
    </HeaderHeightContext.Provider>
  );
}
